import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatKES } from '@/lib/utils'
import type { MemberArrears } from '@riverpark/types'

function TopArrearsTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { payload: MemberArrears }[]
}) {
  if (!active || !payload?.length) return null
  const row = payload[0]!.payload
  return (
    <div className="rounded border border-border-2 bg-card px-3 py-2 shadow-modal">
      <p className="font-sans text-xs font-medium text-ink">{row.memberName}</p>
      <p className="font-sans text-sm font-medium text-destructive">
        {formatKES(row.outstandingAmount)}
      </p>
      <p className="font-sans text-[11px] text-muted-foreground">
        {row.invoiceCount} unpaid invoice{row.invoiceCount === 1 ? '' : 's'}
      </p>
    </div>
  )
}

/** Truncates long member names for the Y-axis label — the tooltip always shows the full name. */
function shortLabel(name: string): string {
  return name.length > 13 ? `${name.slice(0, 12)}…` : name
}

/** A single-line <text> tick — Recharts' default tick wraps long labels onto multiple lines
 * word-by-word, which reads worse than a truncated single line here. */
function YAxisTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill="hsl(var(--ink))">
      {shortLabel(payload?.value ?? '')}
    </text>
  )
}

export function TopArrearsChart({
  data,
  isLoading,
}: {
  data: MemberArrears[]
  isLoading: boolean
}) {
  if (isLoading) return <p className="font-sans text-sm text-muted-foreground">Loading…</p>
  if (data.length === 0) {
    return (
      <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
        No outstanding balances.
      </p>
    )
  }

  // Largest debtor at the top — recharts renders category data bottom-to-top by default.
  const chartData = [...data].reverse()

  return (
    <div
      className="rounded border border-border-2 bg-card p-4"
      style={{ height: chartData.length * 36 + 24 }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border-2))" />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={{ stroke: 'hsl(var(--border-2))' }}
            tickLine={false}
            tickFormatter={(value: number) => formatKES(value)}
          />
          <YAxis
            type="category"
            dataKey="memberName"
            tick={<YAxisTick />}
            axisLine={false}
            tickLine={false}
            width={110}
          />
          <Tooltip content={<TopArrearsTooltip />} cursor={{ fill: 'hsl(var(--cream-2))' }} />
          <Bar dataKey="outstandingAmount" fill="hsl(var(--destructive))" radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
